import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, noop } from "@regrello/core-utils";
import type {
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import type { CellClassParams, ColDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../fields/configure/_internal/ConfigureSpectrumFieldForm";
import {
  getIsFieldInstanceColumn,
  getIsFormFieldColumn,
  type RowDatum,
} from "../../../../../forms/instance/utils/tableV2Utils";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import {
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloFormFieldCheckbox } from "../../../formFields/RegrelloFormFieldCheckbox";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { validateByConstraints } from "../../utils/spectrumFieldConstraintUtils";

type VALUE = boolean; // Saves some characters without using a troublesome generic type.

export class SpectrumCheckboxFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.checkbox";
  }

  public canProcessValidationType(spectrumValidationType: Pick<SpectrumFieldValidationTypeFields, "validationType">) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.BOOLEAN;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "BOOLEAN");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.BOOLEAN,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(_valueConstraints: SpectrumValueConstraintFields[]) {
    return EMPTY_ARRAY;
  }

  public getConstraintConditionOperators() {
    return {
      EQ: {
        label: t`Is checked`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_ZERO: {
        label: t`Is not checked`,
        inputCount: 0,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onChange" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public renderPreviewFormField() {
    return <RegrelloFormFieldCheckbox disabled={true} onChange={noop} value={false} />;
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    return this.renderFormField(props.fieldInstance?.spectrumFieldVersion.field ?? field, props);
  };

  public override parseTableValue(_fieldVersion: SpectrumFieldVersionFields, valueIn: unknown) {
    return valueIn === true || (typeof valueIn === "string" && valueIn.toLowerCase() === "true");
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-center-aligned-cell": (_) => true,
        // (max): Gray cell out if it is not editable.
        "rg-grid-not-editable": (params) => !this.isCellEditable(params),
        // (chase): Prevent pointer events on the checkbox if the cell is not editable.
        "[&_.ag-checkbox-input-wrapper]:pointer-events-none": (params) => !this.isCellEditable(params),
      },
      cellDataType: "boolean",
      cellEditor: "agCheckboxCellEditor", // eslint-disable-line lingui/no-unlocalized-strings
      valueParser: (params) => {
        return this.parseTableValue(options.fieldInstance.spectrumFieldVersion, params.newValue);
      },
      valueGetter: (params: ValueGetterParams<TData, VALUE>) => {
        if (
          params.data == null ||
          params.colDef.colId == null ||
          !(getIsFormFieldColumn(params.colDef.colId) || getIsFieldInstanceColumn(params.colDef.colId))
        ) {
          return false;
        }
        // Accomodate stringified values from bulk paste operations.
        const valueIn = params.data[params.colDef.colId];
        return valueIn === true;
      },
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: VALUE,
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      booleanValue: frontendValue,
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const valueAsString = value.trim();

    const result = validateRequired(valueAsString, options.isRequired);
    if (result != null) {
      return result;
    }

    const normalizedValue = valueAsString.toLocaleLowerCase();

    if (normalizedValue !== "true" && normalizedValue !== "false") {
      return {
        value: valueAsString,
        info: [
          {
            message: t`Must be 'true' or 'false' (case-insensitive)`,
            level: "error",
          },
        ],
      };
    }

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, normalizedValue === "true");
    if (!spectrumValidationResult.isValid) {
      return {
        value: normalizedValue,
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return {
      value: normalizedValue,
    };
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value != null && typeof value !== "boolean") {
      return {
        isValid: false,
        errors: [t`Must be a boolean`],
      };
    }

    if (value == null || !value) {
      if (isRequired) {
        if (this.canBeRequired?.()) {
          console.warn(
            "Checkbox fields cannot be set to be required in Regrello, but isRequired and canBeRequired are true.",
          );
          return {
            isValid: false,
            errors: [t`This field is required`],
          };
        }
        console.warn(
          "Checkbox fields cannot be set to be required, but isRequired is true.",
          "Expected practice is to evaluate isRequired as isRequired && plugin.canBeRequired?.() when passing to validateValue.",
        );
      }
    }

    return this.validateValueInternal(fieldVersion, value ?? false);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }

  private isCellEditable<TData extends RowDatum>(params: CellClassParams<TData, boolean>): boolean {
    if (params.colDef.editable == null) {
      return false;
    }

    if (typeof params.colDef.editable === "boolean") {
      return params.colDef.editable;
    }

    return params.colDef.editable(params);
  }
}
